import React, { useState, useEffect } from 'react';
import GradeVisibilityCard from '@/pages/admin/GradeVisibilityCard';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet, apiPut, apiPost, apiDelete, API_BASE, authedFetch } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Users, Brain, BookOpen, MessageCircle, Settings, BarChart3,
         Shield, GraduationCap, TrendingUp, MapPin, Globe, Flame, Award, Zap, Trash2, AlertTriangle, History, Sparkles, FileText, Heart, MessageSquareText, CircleDollarSign, Gamepad2, Image as ImageIcon, Film, School, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import CostDashboard from '@/pages/admin/CostDashboard';
import FinanceDashboard from '@/pages/admin/FinanceDashboard';
import ReferralDashboard from '@/pages/admin/ReferralDashboard';
import CustomSchoolsReport from '@/pages/admin/CustomSchoolsReport';
import ContentLibrary from '@/pages/admin/ContentLibrary';
import EmotionalSafetyDashboard from '@/pages/admin/EmotionalSafetyDashboard';
import FeedbackDashboard from '@/pages/admin/FeedbackDashboard';
import WebsiteAdsManager from '@/pages/admin/WebsiteAdsManager';
import PremiumFlagsPanel from '@/pages/admin/PremiumFlagsPanel';
import HelpCenterPanel from '@/pages/admin/HelpCenterPanel';
import AdminTasksButton from '@/components/admin/AdminTasksButton';
import AccountHygienePanel from '@/components/admin/AccountHygienePanel';
import AiHealthBanner from '@/components/admin/AiHealthBanner';
import { Crown, Library } from 'lucide-react';
import PuzzleSeedForm from '@/components/admin/PuzzleSeedForm';

// Settings used to be one endless scroll. Each card now belongs to exactly one
// category; categories that already own a full tab just point there.
const SETTINGS_CATEGORIES = [
  { key: 'general', label: 'General & Branding' },
  { key: 'trial', label: 'Free Trial & Access' },
  { key: 'content', label: 'Content & Curriculum' },
  { key: 'coins', label: 'Gamification & Coins' },
  { key: 'notifications', label: 'Notifications & Email', elsewhere: 'Content Management → Integrations' },
  { key: 'integrations', label: 'Integrations & Keys', elsewhere: 'Content Management → Integrations' },
  { key: 'toggles', label: 'Feature Toggles' },
];

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtDate(d) { const p = d.split('-'); return p.length >= 3 ? `${MONTHS[parseInt(p[1],10)-1]}-${p[2]}` : d; }
const BAR_COLORS = ['#6366F1','#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#14B8A6'];

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [boards, setBoards] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [subTiers, setSubTiers] = useState(null);
  const [savingTiers, setSavingTiers] = useState(false);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/home'); return; }
    apiGet('/admin/settings').then(setSettings).catch(() => {});
    apiGet('/admin/users').then(d => setUsers(d.users || [])).catch(() => {});
    apiGet('/admin/stats').then(setStats).catch(() => {});
    apiGet('/onboarding/all-boards').then(d => setBoards(d.boards || [])).catch(() => {});
    apiGet('/subscription/tiers').then(d => setSubTiers(d.tiers || {})).catch(() => {});
  }, [user, navigate]);

  const loadAuditLog = async (filter = '') => {
    setAuditLoading(true);
    try {
      const q = filter ? `?target_type=${filter}` : '';
      const d = await apiGet(`/admin/audit-log${q}`);
      setAuditLog(d.items || []);
    } catch { /* ignore */ }
    setAuditLoading(false);
  };

  const handleDeleteUser = async (u) => {
    try {
      const res = await apiDelete(`/admin/users/${u.user_id}`);
      toast.success(`User deleted (${Object.values(res.cascades || {}).reduce((a, b) => a + (b || 0), 0)} related records removed)`);
      setUsers(prev => prev.filter(pu => pu.user_id !== u.user_id));
    } catch (e) {
      toast.error('Delete failed: ' + (e.message || 'unknown'));
    } finally {
      setConfirmDeleteUser(null);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const updated = await apiPut('/admin/settings', {
        daily_limits: settings.daily_limits,
        feature_toggles: settings.feature_toggles,
        marketing_content: settings.marketing_content,
        chapter_content_config: settings.chapter_content_config,
      });
      setSettings(updated); toast.success('Settings saved!');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const updateLimit = (key, value) => setSettings(s => ({ ...s, daily_limits: { ...s.daily_limits, [key]: parseInt(value) || 0 } }));
  const toggleFeature = (key) => setSettings(s => ({ ...s, feature_toggles: { ...s.feature_toggles, [key]: !s.feature_toggles[key] } }));
  const updateMarketing = (key, value) => setSettings(s => ({ ...s, marketing_content: { ...(s.marketing_content || {}), [key]: value } }));
  const updateLegalInfo = (key, value) => setSettings(s => ({ ...s, legal_info: { ...(s.legal_info || {}), [key]: value } }));

  const updatePoolSize = (kind, value) => setSettings(s => ({
    ...s,
    chapter_content_config: {
      ...(s.chapter_content_config || {}),
      pool_size: {
        ...((s.chapter_content_config || {}).pool_size || {}),
        [kind]: Math.max(0, parseInt(value) || 0),
      },
    },
  }));
  const updateTierCap = (tier, contentKey, value) => setSettings(s => ({
    ...s,
    chapter_content_config: {
      ...(s.chapter_content_config || {}),
      tier_caps: {
        ...((s.chapter_content_config || {}).tier_caps || {}),
        [tier]: {
          ...(((s.chapter_content_config || {}).tier_caps || {})[tier] || {}),
          [contentKey]: Math.max(0, parseInt(value) || 0),
        },
      },
    },
  }));
  const updateLegalPage = (slug, field, value) => setSettings(s => ({
    ...s,
    legal_pages: {
      ...(s.legal_pages || {}),
      [slug]: { ...((s.legal_pages || {})[slug] || {}), [field]: value }
    }
  }));

  const [savingLegal, setSavingLegal] = useState(false);
  const saveLegal = async () => {
    setSavingLegal(true);
    try {
      const updated = await apiPut('/admin/settings', {
        legal_info: settings.legal_info,
        legal_pages: settings.legal_pages,
      });
      setSettings(updated); toast.success('Legal content saved!');
    } catch { toast.error('Failed to save legal content'); }
    setSavingLegal(false);
  };
  const [legalActiveTab, setLegalActiveTab] = useState('terms');
  const resetLegalPage = (slug) => {
    // Call backend to fetch DEFAULT_LEGAL_PAGES isn't exposed — instead, clear to empty and warn
    if (!window.confirm('Reset this page to the platform default? Your current edits will be lost.')) return;
    updateLegalPage(slug, 'body', '');
    toast.message('Page cleared. Click Save to persist — the platform will backfill the default template on next reload.');
  };

  const updateTierField = (tierKey, field, value) => {
    setSubTiers(prev => ({
      ...prev,
      [tierKey]: { ...prev[tierKey], [field]: value }
    }));
  };
  const updateTierLimit = (tierKey, limitKey, value) => {
    const numVal = value === 'unlimited' ? 9999 : (parseInt(value) || 0);
    setSubTiers(prev => ({
      ...prev,
      [tierKey]: { ...prev[tierKey], limits: { ...prev[tierKey].limits, [limitKey]: numVal } }
    }));
  };
  const updateTierFeature = (tierKey, idx, value) => {
    setSubTiers(prev => {
      const features = [...(prev[tierKey].features || [])];
      features[idx] = value;
      return { ...prev, [tierKey]: { ...prev[tierKey], features } };
    });
  };
  const addTierFeature = (tierKey) => {
    setSubTiers(prev => ({
      ...prev,
      [tierKey]: { ...prev[tierKey], features: [...(prev[tierKey].features || []), ''] }
    }));
  };
  const removeTierFeature = (tierKey, idx) => {
    setSubTiers(prev => {
      const features = [...(prev[tierKey].features || [])];
      features.splice(idx, 1);
      return { ...prev, [tierKey]: { ...prev[tierKey], features } };
    });
  };
  const [settingsCat, setSettingsCat] = useState('general');
  const showSetting = (key) => settingsCat === key;
  // Hybrid pricing helpers (Phase 2.41) — drive the new "Subscription Settings"
  // tab. Each paid tier has 3 sub-plans (monthly, yearly, lifetime) under
  // tier.billing_plans, and each plan has `enabled` + `price` + `label_suffix`.
  // Admin can toggle any combination — e.g. Growth = monthly + lifetime only,
  // skipping yearly entirely. Public SubscriptionPage renders one CTA per
  // enabled plan; disabled plans are hidden from end users.
  const togglePlanEnabled = (tierKey, planKey, enabled) => {
    setSubTiers(prev => {
      const plans = { ...(prev[tierKey].billing_plans || {}) };
      plans[planKey] = { ...(plans[planKey] || {}), enabled: !!enabled };
      return { ...prev, [tierKey]: { ...prev[tierKey], billing_plans: plans } };
    });
  };
  const updatePlanPrice = (tierKey, planKey, price) => {
    setSubTiers(prev => {
      const plans = { ...(prev[tierKey].billing_plans || {}) };
      plans[planKey] = { ...(plans[planKey] || {}), price: parseInt(price) || 0 };
      return { ...prev, [tierKey]: { ...prev[tierKey], billing_plans: plans } };
    });
  };
  const updatePlanField = (tierKey, planKey, field, value) => {
    setSubTiers(prev => {
      const plans = { ...(prev[tierKey].billing_plans || {}) };
      plans[planKey] = { ...(plans[planKey] || {}), [field]: value };
      return { ...prev, [tierKey]: { ...prev[tierKey], billing_plans: plans } };
    });
  };
  const saveTiers = async () => {
    setSavingTiers(true);
    try {
      // Auto-generate price_label from the cheapest enabled billing_plan so
      // the public Subscription page always shows the most attractive entry
      // point. Also keep top-level `price` synced with that cheapest plan,
      // for any legacy code paths still reading it (back-compat).
      const tiersToSave = { ...subTiers };
      Object.keys(tiersToSave).forEach(k => {
        const t = tiersToSave[k];
        if (t.price === 0 || k === 'basic') {
          t.price_label = 'Free Forever';
        } else {
          const plans = t.billing_plans || {};
          const enabled = ['monthly', 'yearly', 'lifetime']
            .map(pk => ({ k: pk, ...(plans[pk] || {}) }))
            .filter(p => p.enabled && (p.price || 0) > 0);
          if (enabled.length) {
            const cheapest = enabled.reduce((a, b) => (a.price <= b.price ? a : b));
            t.price = cheapest.price;
            const suffix = (cheapest.label_suffix || (cheapest.k === 'lifetime' ? '· lifetime' : (cheapest.k === 'yearly' ? '/year' : '/month'))).trim();
            t.price_label = `Rs ${(cheapest.price || 0).toLocaleString('en-IN')}${suffix.startsWith('·') ? ' ' + suffix : suffix}`;
          } else {
            // No plans enabled — keep legacy price-only label
            const cycle = t.billing_cycle || 'monthly';
            const formatted = (t.price || 0).toLocaleString('en-IN');
            if (cycle === 'lifetime') t.price_label = `Rs ${formatted} · lifetime`;
            else if (cycle === 'yearly') t.price_label = `Rs ${formatted}/year`;
            else t.price_label = `Rs ${formatted}/month`;
          }
        }
      });
      await apiPut('/admin/subscription/tiers', { tiers: tiersToSave });
      if (settings?.payment_config) {
        const updated = await apiPut('/admin/settings', { payment_config: settings.payment_config });
        setSettings(updated);
      }
      toast.success('Subscription tiers saved!');
    } catch { toast.error('Failed to save tiers'); }
    setSavingTiers(false);
  };

  const updatePay = (key, value) => setSettings(s => ({ ...s, payment_config: { ...(s.payment_config || {}), [key]: value } }));

  // Razorpay link fields per tier — rendered inside each tier block in the
  // Subscription Settings tab so pricing and its checkout link stay together.
  const TIER_LINK_CONFIG = {
    growth: {
      title: 'Growth — Razorpay Subscription Link',
      hint: 'When ON, the landing-page "Get Growth" button opens this link directly instead of the in-app subscription page.',
      accent: 'border-emerald-200 bg-emerald-50/40 text-emerald-800',
      switchKey: 'growth_subscription_enabled', switchClass: 'data-[state=checked]:bg-emerald-500',
      testPrefix: 'growth-sub',
      fields: [
        { key: 'growth_subscription_label', label: 'Button label (optional — defaults to "Get Growth")', placeholder: 'Get Growth · ₹249/mo' },
        { key: 'growth_subscription_url', label: 'Razorpay Subscription Link URL', placeholder: 'https://rzp.io/rzp/xxxxxxx', full: true, test: 'growth-sub-url-input' },
      ],
    },
    premium: {
      title: 'Premium — Razorpay Subscription Link',
      hint: 'When ON, the landing-page "Get Premium" button opens this link. Leave the URL blank until the live link is ready — nothing can be charged while blank.',
      accent: 'border-amber-200 bg-amber-50/40 text-amber-800',
      switchKey: 'premium_subscription_enabled', switchClass: 'data-[state=checked]:bg-amber-500',
      testPrefix: 'premium-sub',
      fields: [
        { key: 'premium_display_price_inr', label: 'Display price (₹/month) — shown on the Premium card', number: true, test: 'premium-display-price-input' },
        { key: 'premium_subscription_label', label: 'Button label (optional — defaults to "Get Premium")', placeholder: 'Get Premium · ₹499/mo' },
        { key: 'premium_subscription_url', label: 'Razorpay Subscription Link URL', placeholder: 'https://rzp.io/rzp/xxxxxxx', full: true, test: 'premium-sub-url-input' },
      ],
    },
    elite: {
      title: 'Elite Lifetime Offer — Razorpay Payment Link',
      hint: 'When ON, the Elite tier button on the landing page becomes the lifetime offer and opens this Razorpay Payment Link.',
      accent: 'border-purple-200 bg-purple-50/40 text-purple-800',
      switchKey: 'lifetime_offer_enabled', switchClass: 'data-[state=checked]:bg-purple-500',
      testPrefix: 'lifetime-offer',
      fields: [
        { key: 'lifetime_offer_label', label: 'Button label', placeholder: '₹5,999 - Lifetime Offer', test: 'lifetime-offer-label-input' },
        { key: 'lifetime_offer_price_inr', label: 'Price (₹) — for your reference', number: true, test: 'lifetime-offer-price-input' },
        { key: 'lifetime_offer_url', label: 'Razorpay Payment Link URL (paste the full https:// link)', placeholder: 'https://rzp.io/rzp/xxxxxxx  or  https://razorpay.me/@yourbrand', full: true, test: 'lifetime-offer-url-input' },
        { key: 'lifetime_offer_note', label: 'Note shown below the button', placeholder: 'Independence Day Offer · first 499 students only', full: true, test: 'lifetime-offer-note-input' },
      ],
    },
  };

  const renderTierPaymentLink = (tierKey) => {
    const cfg = TIER_LINK_CONFIG[tierKey];
    const pc = settings?.payment_config;
    if (!cfg || !pc) return null;
    return (
      <div className={`mt-4 p-4 rounded-xl border-2 ${cfg.accent}`} data-testid={`tier-payment-link-${tierKey}`}>
        <div className="flex items-center justify-between mb-3 gap-3">
          <div>
            <Label className="text-sm font-heading">{cfg.title}</Label>
            <p className="text-[11px] font-body opacity-80">{cfg.hint}</p>
          </div>
          <Switch data-testid={`${cfg.testPrefix}-enabled-switch`}
            checked={!!pc[cfg.switchKey]}
            onCheckedChange={(v) => updatePay(cfg.switchKey, v)}
            className={cfg.switchClass} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {cfg.fields.map(f => (
            <div key={f.key} className={f.full ? 'md:col-span-2' : ''}>
              <Label className="text-xs font-body text-slate-500">{f.label}</Label>
              <Input data-testid={f.test || `${cfg.testPrefix}-${f.key}-input`}
                type={f.number ? 'number' : 'text'}
                min={f.number ? '0' : undefined}
                placeholder={f.placeholder}
                value={pc[f.key] ?? ''}
                onChange={(e) => updatePay(f.key, f.number ? (parseInt(e.target.value) || 0) : e.target.value)}
                className="rounded-lg font-body text-sm mt-1 bg-white" />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const toggleBoardVisibility = async (boardId) => {
    try {
      const res = await authedFetch(`${API_BASE}/admin/boards/${boardId}/visibility`, { method: 'PUT' });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setBoards(prev => prev.map(b => b.board_id === boardId ? updated : b));
      toast.success('Board visibility updated');
    } catch { toast.error('Failed to update board'); }
  };

  const filteredUsers = searchQuery
    ? users.filter(u => u.name?.toLowerCase().includes(searchQuery.toLowerCase()) || u.email?.toLowerCase().includes(searchQuery.toLowerCase()))
    : users;

  if (!settings) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const s = stats || {};

  return (
    <div className="min-h-screen bg-[#F8F9FC]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <button data-testid="admin-back-btn" onClick={() => navigate('/home')} className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div><h1 className="font-heading text-lg font-bold text-slate-900">Admin Command Center</h1><p className="text-xs text-slate-400 font-body">Root Admin Analytics & Controls</p></div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <AdminTasksButton />
          <Badge className="bg-purple-50 text-purple-600"><Shield className="w-3 h-3 mr-1" />Root Admin</Badge>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <AiHealthBanner />

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 rounded-2xl p-1 flex flex-wrap justify-start gap-1 h-auto w-full" data-testid="admin-tabs">
            <TabsTrigger value="overview" className="rounded-xl font-heading text-xs" data-testid="tab-overview"><BarChart3 className="w-3.5 h-3.5 mr-1" />Overview</TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-xl font-heading text-xs" data-testid="tab-analytics"><TrendingUp className="w-3.5 h-3.5 mr-1" />Analytics</TabsTrigger>
            <TabsTrigger value="users" className="rounded-xl font-heading text-xs" data-testid="tab-users"><Users className="w-3.5 h-3.5 mr-1" />Users</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-xl font-heading text-xs" data-testid="tab-settings"><Settings className="w-3.5 h-3.5 mr-1" />Settings</TabsTrigger>
            <TabsTrigger value="pricing" className="rounded-xl font-heading text-xs data-[state=active]:text-emerald-600" data-testid="tab-pricing"><CircleDollarSign className="w-3.5 h-3.5 mr-1" />Subscription Settings</TabsTrigger>
            <TabsTrigger value="cost" className="rounded-xl font-heading text-xs" data-testid="tab-cost"><Zap className="w-3.5 h-3.5 mr-1" />Cost & Usage</TabsTrigger>
            <TabsTrigger value="finance" className="rounded-xl font-heading text-xs" data-testid="tab-finance"><CircleDollarSign className="w-3.5 h-3.5 mr-1" />Finance</TabsTrigger>
            <TabsTrigger value="referrals" className="rounded-xl font-heading text-xs data-[state=active]:text-emerald-600" data-testid="tab-referrals"><Award className="w-3.5 h-3.5 mr-1" />Referrals</TabsTrigger>
            <TabsTrigger value="schools" className="rounded-xl font-heading text-xs data-[state=active]:text-blue-600" data-testid="tab-schools"><School className="w-3.5 h-3.5 mr-1" />Schools</TabsTrigger>
            <TabsTrigger value="website-ads" className="rounded-xl font-heading text-xs data-[state=active]:text-indigo-600" data-testid="tab-website-ads"><Megaphone className="w-3.5 h-3.5 mr-1" />Website Ads</TabsTrigger>
            <TabsTrigger value="legal" className="rounded-xl font-heading text-xs" data-testid="tab-legal"><FileText className="w-3.5 h-3.5 mr-1" />Legal</TabsTrigger>
            <TabsTrigger value="safety" className="rounded-xl font-heading text-xs data-[state=active]:text-rose-600" data-testid="tab-safety"><Heart className="w-3.5 h-3.5 mr-1" />Safety</TabsTrigger>
            <TabsTrigger value="feedback" className="rounded-xl font-heading text-xs data-[state=active]:text-pink-600" data-testid="tab-feedback"><MessageSquareText className="w-3.5 h-3.5 mr-1" />Feedback</TabsTrigger>
            <TabsTrigger value="premium" className="rounded-xl font-heading text-xs data-[state=active]:text-fuchsia-600" data-testid="tab-premium"><Crown className="w-3.5 h-3.5 mr-1" />Premium</TabsTrigger>
            <TabsTrigger value="audit" className="rounded-xl font-heading text-xs" data-testid="tab-audit" onClick={() => loadAuditLog(auditFilter)}><History className="w-3.5 h-3.5 mr-1" />Audit Log</TabsTrigger>
          </TabsList>

          {/* ==================== OVERVIEW TAB ==================== */}
          <TabsContent value="overview" className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 stagger-children">
              {[
                { label: 'Total Users', value: s.total_users || 0, icon: Users, color: 'text-blue-500 bg-blue-50' },
                { label: 'Active Today', value: s.active_today || 0, icon: Zap, color: 'text-emerald-500 bg-emerald-50' },
                { label: 'Active (7d)', value: s.active_week || 0, icon: TrendingUp, color: 'text-purple-500 bg-purple-50' },
                { label: 'Active (30d)', value: s.active_month || 0, icon: Flame, color: 'text-orange-500 bg-orange-50' },
                { label: 'New (7d)', value: s.new_signups_week || 0, icon: Award, color: 'text-cyan-500 bg-cyan-50' },
                { label: 'Total Messages', value: s.total_messages || 0, icon: MessageCircle, color: 'text-amber-500 bg-amber-50' },
              ].map(m => (
                <Card key={m.label} className="p-4 rounded-2xl border-slate-100" data-testid={`metric-${m.label.toLowerCase().replace(/[\s()]/g, '-')}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${m.color} mb-2`}><m.icon className="w-4.5 h-4.5" /></div>
                  <p className="font-heading text-2xl font-bold text-slate-800">{m.value}</p>
                  <p className="text-xs text-slate-500 font-body">{m.label}</p>
                </Card>
              ))}
            </div>

            {/* Feature Usage */}
            <Card className="p-5 rounded-2xl border-slate-100">
              <h3 className="font-heading text-base font-semibold text-slate-800 mb-4">Feature Usage</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Chat Sessions', value: s.feature_usage?.chat_sessions || 0, icon: MessageCircle, color: 'bg-blue-50 text-blue-600', title: 'Total buddy conversations started', testid: 'feature-usage-chat-sessions' },
                  { label: 'Chat Messages', value: s.feature_usage?.chat_messages || 0, icon: MessageCircle, color: 'bg-purple-50 text-purple-600', title: 'Total messages exchanged across all sessions', testid: 'feature-usage-chat-messages' },
                  { label: 'Quizzes Taken', value: s.feature_usage?.quizzes_taken || 0, icon: Brain, color: 'bg-emerald-50 text-emerald-600', title: 'Quiz attempts completed by students', testid: 'feature-usage-quizzes-taken' },
                  { label: 'Flashcard Sets', value: s.feature_usage?.flashcard_sets || 0, icon: BookOpen, color: 'bg-amber-50 text-amber-600', title: 'Flashcard sets studied', testid: 'feature-usage-flashcard-sets' },
                  { label: 'Puzzles Solved', value: s.feature_usage?.puzzles_solved || 0, icon: Gamepad2, color: 'bg-rose-50 text-rose-600', title: 'Puzzle mazes completed to the treasure tile', testid: 'feature-usage-puzzles-solved' },
                  { label: 'Infographics Viewed', value: s.feature_usage?.infographic_views || 0, icon: ImageIcon, color: 'bg-cyan-50 text-cyan-600', title: 'Times a student opened an infographic', testid: 'feature-usage-infographic-views' },
                  { label: 'Cinegraphics Viewed', value: s.feature_usage?.cinegraphic_views || 0, icon: Film, color: 'bg-violet-50 text-violet-600', title: 'Times a student played a cinegraphic video', testid: 'feature-usage-cinegraphic-views' },
                ].map(f => (
                  <div key={f.label}
                    title={f.title}
                    data-testid={f.testid}
                    className={`p-4 rounded-xl ${f.color.split(' ')[0]} flex items-center gap-3 transition-all duration-200 hover:scale-[1.03] hover:shadow-md cursor-default`}>
                    <f.icon className={`w-5 h-5 ${f.color.split(' ')[1]}`} />
                    <div><p className={`font-heading text-xl font-bold ${f.color.split(' ')[1]}`}>{f.value}</p><p className="text-xs opacity-70 font-body">{f.label}</p></div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Signup Trend */}
            <SignupCard initialData={s.signup_trend} />
          </TabsContent>

          {/* ==================== ANALYTICS TAB ==================== */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Geographic Distribution */}
              <Card className="p-5 rounded-2xl border-slate-100">
                <div className="flex items-center gap-2 mb-4"><MapPin className="w-5 h-5 text-blue-500" /><h3 className="font-heading text-base font-semibold text-slate-800">Users by State</h3></div>
                {(s.geographic || []).length === 0 ? <p className="text-sm text-slate-400 font-body text-center py-6">No geographic data yet</p> : (
                  <div className="space-y-2">
                    {s.geographic.map((g, i) => (
                      <div key={g.state} className="flex items-center gap-3">
                        <span className="w-5 text-xs text-slate-400 font-body text-right">{i + 1}</span>
                        <div className="flex-1"><div className="flex justify-between text-sm font-body mb-0.5"><span className="text-slate-700">{g.state}</span><span className="text-slate-500">{g.count}</span></div>
                          <div className="h-1.5 bg-slate-100 rounded-full"><div className="h-full rounded-full" style={{ width: `${(g.count / (s.geographic[0]?.count || 1)) * 100}%`, background: BAR_COLORS[i % BAR_COLORS.length] }} /></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* City Distribution */}
              <Card className="p-5 rounded-2xl border-slate-100">
                <div className="flex items-center gap-2 mb-4"><Globe className="w-5 h-5 text-emerald-500" /><h3 className="font-heading text-base font-semibold text-slate-800">Users by City</h3></div>
                {(s.cities || []).length === 0 ? <p className="text-sm text-slate-400 font-body text-center py-6">No city data yet</p> : (
                  <div className="space-y-2">
                    {s.cities.map((c, i) => (
                      <div key={c.city} className="flex items-center gap-3">
                        <span className="w-5 text-xs text-slate-400 font-body text-right">{i + 1}</span>
                        <div className="flex-1"><div className="flex justify-between text-sm font-body mb-0.5"><span className="text-slate-700">{c.city}</span><span className="text-slate-500">{c.count}</span></div>
                          <div className="h-1.5 bg-slate-100 rounded-full"><div className="h-full rounded-full" style={{ width: `${(c.count / (s.cities[0]?.count || 1)) * 100}%`, background: BAR_COLORS[(i + 3) % BAR_COLORS.length] }} /></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Content Performance */}
              <Card className="p-5 rounded-2xl border-slate-100">
                <div className="flex items-center gap-2 mb-4"><Brain className="w-5 h-5 text-purple-500" /><h3 className="font-heading text-base font-semibold text-slate-800">Popular Chat Topics</h3></div>
                {(s.content_performance || []).length === 0 ? <p className="text-sm text-slate-400 font-body text-center py-6">No content data yet</p> : (
                  <div className="space-y-2">
                    {s.content_performance.map((t, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50">
                        <div><p className="font-heading text-sm font-medium text-slate-700">{t.topic}</p><p className="text-xs text-slate-400 font-body">{t.subject}</p></div>
                        <Badge className="bg-purple-50 text-purple-600 text-xs">{t.sessions} sessions</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Quiz Performance / Struggle Areas */}
              <Card className="p-5 rounded-2xl border-slate-100">
                <div className="flex items-center gap-2 mb-4"><Brain className="w-5 h-5 text-amber-500" /><h3 className="font-heading text-base font-semibold text-slate-800">Quiz Performance by Topic</h3></div>
                {(s.quiz_performance || []).length === 0 ? <p className="text-sm text-slate-400 font-body text-center py-6">No quiz data yet</p> : (
                  <div className="space-y-2">
                    {s.quiz_performance.map((q, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50">
                        <div><p className="font-heading text-sm font-medium text-slate-700">{q.topic}</p><p className="text-xs text-slate-400 font-body">{q.subject} &middot; {q.attempts} attempts</p></div>
                        <Badge className={`text-xs ${q.avg_score >= 70 ? 'bg-emerald-50 text-emerald-600' : q.avg_score >= 40 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>Avg: {q.avg_score}%</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Board Distribution */}
              <Card className="p-5 rounded-2xl border-slate-100">
                <div className="flex items-center gap-2 mb-4"><GraduationCap className="w-5 h-5 text-blue-500" /><h3 className="font-heading text-base font-semibold text-slate-800">Users by Board</h3></div>
                {(s.board_distribution || []).length === 0 ? <p className="text-sm text-slate-400 font-body text-center py-6">No data yet</p> : (
                  <div className="space-y-2">{s.board_distribution.map((b, i) => (
                    <div key={b.board} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                      <span className="text-sm font-body text-slate-700">{b.board}</span><Badge className="bg-blue-50 text-blue-600 text-xs">{b.count}</Badge>
                    </div>
                  ))}</div>
                )}
              </Card>

              {/* Class Distribution */}
              <Card className="p-5 rounded-2xl border-slate-100">
                <div className="flex items-center gap-2 mb-4"><BookOpen className="w-5 h-5 text-emerald-500" /><h3 className="font-heading text-base font-semibold text-slate-800">Users by Class</h3></div>
                {(s.class_distribution || []).length === 0 ? <p className="text-sm text-slate-400 font-body text-center py-6">No data yet</p> : (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">{s.class_distribution.map(c => (
                    <div key={c.class_level} className="p-3 rounded-xl bg-emerald-50 text-center">
                      <p className="font-heading text-lg font-bold text-emerald-700">{c.count}</p>
                      <p className="text-xs text-emerald-600 font-body">Class {c.class_level}</p>
                    </div>
                  ))}</div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ==================== USERS TAB ==================== */}
          <TabsContent value="users" className="space-y-6">
            <Card className="rounded-2xl border-slate-100 overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                <Input data-testid="user-search" placeholder="Search by name or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="max-w-sm rounded-xl font-body" />
                <span className="text-xs text-slate-400 font-body">{filteredUsers.length} users</span>
              </div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="font-heading text-xs">Name</TableHead>
                  <TableHead className="font-heading text-xs">Email</TableHead>
                  <TableHead className="font-heading text-xs">Board</TableHead>
                  <TableHead className="font-heading text-xs">Class</TableHead>
                  <TableHead className="font-heading text-xs">State</TableHead>
                  <TableHead className="font-heading text-xs">Points</TableHead>
                  <TableHead className="font-heading text-xs">Role</TableHead>
                  <TableHead className="font-heading text-xs text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filteredUsers.slice(0, 50).map(u => (
                    <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <TableCell className="font-body text-sm">{u.name}</TableCell>
                      <TableCell className="font-body text-xs text-slate-500">{u.email}</TableCell>
                      <TableCell className="font-body text-xs">{u.board || '-'}</TableCell>
                      <TableCell className="font-body text-xs">{u.class_level || '-'}</TableCell>
                      <TableCell className="font-body text-xs">{u.state || '-'}</TableCell>
                      <TableCell className="font-heading text-sm font-semibold">{u.total_points || 0}</TableCell>
                      <TableCell>
                        <select data-testid={`role-select-${u.user_id}`} value={u.role || 'student'}
                          onChange={async (e) => {
                            const newRole = e.target.value;
                            const prevRole = u.role || 'student';
                            // Optimistically update the UI first so the dropdown reflects the new role immediately
                            setUsers(prev => prev.map(pu => pu.user_id === u.user_id ? { ...pu, role: newRole } : pu));
                            try {
                              await apiPost('/admin/update-role', { user_id: u.user_id, role: newRole });
                              const roleLabel = { student: 'Student', parent: 'Parent', support_staff: 'Support Staff', admin: 'Admin' }[newRole] || newRole;
                              toast.success(`Role updated to ${roleLabel}`);
                            } catch {
                              // Roll back on failure
                              setUsers(prev => prev.map(pu => pu.user_id === u.user_id ? { ...pu, role: prevRole } : pu));
                              toast.error('Failed to update role');
                            }
                          }}
                          className={`text-xs font-heading px-2 py-1 rounded-lg border ${u.role === 'admin' ? 'border-purple-200 bg-purple-50 text-purple-600' : u.role === 'parent' ? 'border-blue-200 bg-blue-50 text-blue-600' : u.role === 'support_staff' ? 'border-amber-200 bg-amber-50 text-amber-600' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
                          <option value="student">Student</option>
                          <option value="parent">Parent</option>
                          <option value="support_staff">Support Staff</option>
                          <option value="admin">Admin</option>
                        </select>
                      </TableCell>
                      <TableCell className="text-right">
                        {u.user_id === user?.user_id ? (
                          <span className="text-[10px] text-slate-300 font-body italic">Self</span>
                        ) : (
                          <button data-testid={`delete-user-${u.user_id}`} onClick={() => setConfirmDeleteUser(u)}
                            title="Delete user (Admin only)"
                            className="w-8 h-8 rounded-lg text-red-500 hover:bg-red-500 hover:text-white inline-flex items-center justify-center transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <AccountHygienePanel />
          </TabsContent>

          {/* ==================== SETTINGS TAB ==================== */}
          <TabsContent value="premium" className="space-y-6">
            <PremiumFlagsPanel />
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            {/* Settings used to be one endless page. These categories keep each
                knob findable; the pills filter the cards below. */}
            <div className="flex flex-wrap gap-2" data-testid="settings-category-nav">
              {SETTINGS_CATEGORIES.map(c => (
                <button key={c.key} type="button" data-testid={`settings-cat-${c.key}`} data-pill={c.key}
                  onClick={() => setSettingsCat(c.key)}
                  className={`px-3.5 py-1.5 rounded-full text-[12px] font-heading transition-all border ${settingsCat === c.key ? 'bg-slate-900 text-white border-slate-900 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                  {c.label}
                </button>
              ))}
            </div>
            {(() => {
              const cat = SETTINGS_CATEGORIES.find(c => c.key === settingsCat);
              return cat?.elsewhere ? (
                <Card className="p-5 rounded-2xl border-dashed border-slate-300 bg-slate-50" data-testid="settings-cat-pointer">
                  <p className="text-sm font-body text-slate-600">
                    {cat.label} lives in its own tab — <strong>{cat.elsewhere}</strong> — at the top of the Command Center,
                    so the full screen is available for it.
                  </p>
                </Card>
              ) : null;
            })()}

            {/* Feature Toggles - Per Subscription Tier */}
            {showSetting('toggles') && (
            <Card className="p-6 rounded-2xl border-slate-100">
              <h3 className="font-heading text-base font-semibold text-slate-800 mb-1">Feature Toggles</h3>
              <p className="text-xs text-slate-500 font-body mb-4">Control which features are available per subscription tier</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 pr-4 font-heading text-xs text-slate-500 font-semibold">Feature</th>
                      <th className="text-center py-2 px-2 font-heading text-xs text-slate-500 font-semibold">Global</th>
                      <th className="text-center py-2 px-2 font-heading text-xs text-blue-500 font-semibold">Basic</th>
                      <th className="text-center py-2 px-2 font-heading text-xs text-indigo-500 font-semibold">Growth</th>
                      <th className="text-center py-2 px-2 font-heading text-xs text-amber-500 font-semibold">Premium</th>
                      <th className="text-center py-2 px-2 font-heading text-xs text-purple-500 font-semibold">Elite</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: 'leaderboard_enabled', label: 'Leaderboard', desc: 'May cause anxiety for some kids' },
                      { key: 'eli5_enabled', label: 'Simple Mode', desc: 'Simplified explanations' },
                      { key: 'snap_solve_enabled', label: 'Snap & Solve', desc: 'Image upload' },
                      { key: 'quiz_enabled', label: 'Quizzes', desc: 'Quiz generation' },
                      { key: 'flashcards_enabled', label: 'Flashcards', desc: 'Flashcard sets' },
                      { key: 'voice_tts_enabled', label: 'Voice Tutor - English (TTS)', desc: 'Listen to responses' },
                      { key: 'voice_premium_enabled', label: 'Voice Tutor - Regional Language', desc: 'Regional languages orb button' },
                      { key: 'infographics_enabled', label: 'Infographic Library', desc: 'Visual content' },
                      { key: 'infographic_generate_enabled', label: 'AI Infographic Generator', desc: 'Nano Banana generation button' },
                      { key: 'chat_illustrations_enabled', label: 'Chat Auto-Illustrations', desc: 'Attaches textbook page / AI diagram to long replies' },
                      { key: 'chat_cache_enabled', label: 'Chat Response Cache', desc: 'Exact + semantic cache — big cost saver' },
                      { key: 'cinegraphics_enabled', label: 'Cinegraphic Library', desc: 'Video content' },
                      { key: 'share_cards_enabled', label: 'Share Cards', desc: 'Growth+ shareable watermarked cards' },
                      { key: 'payments_enabled', label: 'Cashfree Payments', desc: 'Enable after keys are added' },
                      { key: 'landing_page_enabled', label: 'Marketing Landing Page', desc: 'Show marketing site at root /' },
                      { key: 'parent_reports_enabled', label: 'Parent Reports', desc: 'Progress reports' },
                      { key: 'syllabus_upload_enabled', label: 'Syllabus Upload', desc: 'Custom content' },
                    ].map(t => (
                      <tr key={t.key} className="border-b border-slate-50" data-testid={`toggle-row-${t.key}`}>
                        <td className="py-2.5 pr-4">
                          <p className="font-heading text-sm font-medium text-slate-700">{t.label}</p>
                          <p className="text-[10px] text-slate-400 font-body">{t.desc}</p>
                        </td>
                        <td className="text-center py-2.5 px-2">
                          <Switch data-testid={`switch-global-${t.key}`}
                            checked={settings?.feature_toggles?.[t.key] ?? true}
                            onCheckedChange={() => toggleFeature(t.key)}
                            className="data-[state=checked]:bg-slate-500 scale-75" />
                        </td>
                        {['basic', 'growth', 'premium', 'elite'].map(tier => (
                          <td key={tier} className="text-center py-2.5 px-2">
                            <Switch data-testid={`switch-${tier}-${t.key}`}
                              checked={subTiers?.[tier]?.feature_toggles?.[t.key] ?? true}
                              onCheckedChange={() => {
                                setSubTiers(prev => ({
                                  ...prev,
                                  [tier]: {
                                    ...prev[tier],
                                    feature_toggles: {
                                      ...(prev[tier]?.feature_toggles || {}),
                                      [t.key]: !(prev[tier]?.feature_toggles?.[t.key] ?? true)
                                    }
                                  }
                                }));
                              }}
                              className={`scale-75 ${tier === 'basic' ? 'data-[state=checked]:bg-blue-500' : tier === 'growth' ? 'data-[state=checked]:bg-indigo-500' : tier === 'premium' ? 'data-[state=checked]:bg-amber-500' : 'data-[state=checked]:bg-purple-500'}`} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            )}

            {/* Marketing Content — Pricing Section */}
            {showSetting('general') && (<>
            <Card className="p-6 rounded-2xl border-slate-100" data-testid="marketing-content-card">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <h3 className="font-heading text-base font-semibold text-slate-800">Marketing Content · Pricing Section</h3>
              </div>
              <p className="text-xs text-slate-500 font-body mb-5">
                Edit the copy shown above the pricing cards on the landing page. Use this for seasonal offers, campaigns, or repositioning without redeploying.
              </p>
              {settings?.marketing_content && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'pricing_eyebrow', label: 'Eyebrow label', placeholder: 'Pricing' },
                    { key: 'pricing_subtitle', label: 'Subtitle', placeholder: 'Transparent. Student-friendly. Cancel anytime.' },
                    { key: 'pricing_heading_prefix', label: 'Headline · main', placeholder: 'Start Learning Free. Upgrade', full: true },
                    { key: 'pricing_heading_highlight', label: 'Headline · highlight (green gradient)', placeholder: 'only when you love it.', full: true },
                    { key: 'basic_price_label', label: 'Basic price label', placeholder: 'Free' },
                    { key: 'basic_price_suffix', label: 'Basic price suffix (optional)', placeholder: '' },
                    { key: 'basic_cta_label', label: 'Basic CTA button text', placeholder: 'Get My Personal Tutor' },
                    { key: 'paid_cta_prefix', label: 'Paid CTA prefix', placeholder: 'Get' },
                    { key: 'paid_soon_label', label: 'Paid tiers — "coming soon" text', placeholder: 'Launching soon' },
                    { key: 'pricing_footnote', label: 'Footnote (when payments off)', placeholder: '', full: true },
                  ].map(f => (
                    <div key={f.key} className={f.full ? 'sm:col-span-2' : ''} data-testid={`mkt-field-${f.key}`}>
                      <Label className="text-xs font-body text-slate-600">{f.label}</Label>
                      <Input
                        data-testid={`mkt-input-${f.key}`}
                        value={settings.marketing_content[f.key] ?? ''}
                        placeholder={f.placeholder}
                        onChange={e => updateMarketing(f.key, e.target.value)}
                        className="mt-1 rounded-xl font-body" />
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-slate-400 font-body italic mt-4">
                Tip: Keep the headline crisp (under 70 chars). The highlight portion renders in the emerald-teal gradient.
              </p>
            </Card>

            {/* Daily Limits */}
            <Card className="p-6 rounded-2xl border-slate-100">
              <h3 className="font-heading text-base font-semibold text-slate-800 mb-4">Daily Limits (Basic Plan)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { key: 'quiz_per_day', label: 'Quizzes per Day' },
                  { key: 'flashcard_sets_per_day', label: 'Flashcard Sets per Day' },
                  { key: 'snap_solve_per_day', label: 'Snap & Solve per Day' },
                  { key: 'chat_messages_per_day', label: 'Chat Messages per Day' },
                  { key: 'illustrations_per_user_per_day', label: 'Chat Illustrations per User per Day', hint: 'Cap on Nano Banana generations (textbook pages don\'t count)' },
                ].map(l => (
                  <div key={l.key} data-testid={`limit-${l.key}`}>
                    <Label className="text-xs font-body text-slate-600">{l.label}</Label>
                    <Input data-testid={`input-${l.key}`} type="number" value={settings.daily_limits?.[l.key] || 0} onChange={e => updateLimit(l.key, e.target.value)} className="mt-1 rounded-xl font-body" />
                    {l.hint && <p className="text-[10px] text-slate-400 font-body italic mt-1">{l.hint}</p>}
                  </div>
                ))}
              </div>
            </Card>
            </>)}

            {/* Per-Chapter Content Pool & Tier Visibility */}
            {showSetting('content') && (<>
            <Card className="p-6 rounded-2xl border-slate-100" data-testid="chapter-content-config-card">
              <div className="mb-1">
                <h3 className="font-heading text-base font-semibold text-slate-800">Chapter Content Pool & Tier Caps</h3>
                <p className="text-[11px] text-slate-500 font-body mt-0.5">
                  When you click "Pre-generate missing" in the Content Tree, this is how many quizzes & flashcards we generate per chapter — and how many each subscription tier can access.
                </p>
              </div>

              {/* Pool size */}
              <div className="mt-4">
                <p className="text-xs font-heading uppercase tracking-wider text-slate-500 mb-2">Pool size (per chapter)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div data-testid="pool-size-quiz">
                    <Label className="text-xs font-body text-slate-600">Quizzes generated per chapter</Label>
                    <Input
                      data-testid="input-pool-size-quiz"
                      type="number" min="1" max="20"
                      value={settings.chapter_content_config?.pool_size?.quiz ?? 5}
                      onChange={e => updatePoolSize('quiz', e.target.value)}
                      className="mt-1 rounded-xl font-body" />
                  </div>
                  <div data-testid="pool-size-flashcard">
                    <Label className="text-xs font-body text-slate-600">Flashcard sets generated per chapter</Label>
                    <Input
                      data-testid="input-pool-size-flashcard"
                      type="number" min="1" max="30"
                      value={settings.chapter_content_config?.pool_size?.flashcard ?? 10}
                      onChange={e => updatePoolSize('flashcard', e.target.value)}
                      className="mt-1 rounded-xl font-body" />
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 font-body italic mt-2">
                  Tip: bigger pool = more variety per student, but uses more LLM credits during pre-warm. Defaults are 5 quizzes and 10 flashcard sets.
                </p>
              </div>

              {/* Per-Tier Caps Table */}
              <div className="mt-6">
                <p className="text-xs font-heading uppercase tracking-wider text-slate-500 mb-2">Per-chapter access by tier</p>
                <p className="text-[11px] text-slate-500 font-body mb-3">
                  How many of the generated pool each subscription tier can see per chapter. Use a large number (e.g. 999) for "unlimited / all".
                </p>
                <div className="overflow-x-auto rounded-xl border border-slate-100">
                  <table className="w-full text-xs font-body">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 font-heading text-slate-600">Tier</th>
                        <th className="text-left p-2 font-heading text-slate-600">Quizzes / chapter</th>
                        <th className="text-left p-2 font-heading text-slate-600">Flashcard sets / chapter</th>
                        <th className="text-left p-2 font-heading text-slate-600">Infographics / chapter</th>
                        <th className="text-left p-2 font-heading text-slate-600">Cinegraphics / chapter</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: 'basic', label: 'Basic (free)' },
                        { key: 'growth', label: 'Growth' },
                        { key: 'premium', label: 'Premium' },
                        { key: 'elite', label: 'Elite' },
                      ].map(t => {
                        const row = settings.chapter_content_config?.tier_caps?.[t.key] || {};
                        return (
                          <tr key={t.key} className="border-t border-slate-100" data-testid={`tier-cap-row-${t.key}`}>
                            <td className="p-2 font-heading text-slate-700">{t.label}</td>
                            {['quizzes', 'flashcards', 'infographics', 'cinegraphics'].map(c => (
                              <td key={c} className="p-2">
                                <Input
                                  data-testid={`input-tier-cap-${t.key}-${c}`}
                                  type="number" min="0" max="999"
                                  value={row[c] ?? 0}
                                  onChange={e => updateTierCap(t.key, c, e.target.value)}
                                  className="w-24 h-8 rounded-lg font-body text-xs" />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-[10px] text-slate-400 font-body italic mt-2">
                  Note: For Infographics, the cap controls how many you'll see per chapter. To pick <em>which</em> infographic shows for each tier, open <strong>Content Library</strong> and edit the per-row "Visible to tiers" checkboxes.
                </p>
              </div>
            </Card>

            <Button data-testid="save-settings-btn" onClick={saveSettings} disabled={saving} className="bg-blue-500 hover:bg-blue-600 text-white rounded-full font-heading px-8">
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
            </>)}

            {showSetting('general') && <HelpCenterPanel />}

            {/* Trial & Payments Configuration */}
            {showSetting('trial') && settings?.payment_config && (
              <Card className="p-6 rounded-2xl border-slate-100" data-testid="payment-config-card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-base font-semibold text-slate-800">Free Trial &amp; Access Window</h3>
                    <p className="text-xs text-slate-500 font-body">One date, one message — everything students see (landing page, login, app header, reminders) is generated from the fields below. Razorpay payment links now live under <strong>Subscription Settings</strong>.</p>
                  </div>
                  <Button data-testid="save-payment-config-btn" size="sm"
                    onClick={async () => {
                      try {
                        const updated = await apiPut('/admin/settings', { payment_config: settings.payment_config });
                        setSettings(updated);
                        toast.success('Trial settings saved — live everywhere');
                      } catch { toast.error('Failed to save trial settings'); }
                    }}
                    className="bg-amber-500 hover:bg-amber-600 text-white rounded-full font-heading text-xs">
                    Save Trial Settings
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
                    <div>
                      <Label className="text-sm font-heading text-slate-700">Festival / launch free-access window</Label>
                      <p className="text-[11px] text-slate-400 font-body">When ON, <strong>every</strong> student gets the highest plan free until the date beside — this overrides the per-signup trial.</p>
                    </div>
                    <Switch data-testid="trial-enabled-switch"
                      checked={!!settings.payment_config.trial_enabled}
                      onCheckedChange={(v) => setSettings(s => ({ ...s, payment_config: { ...s.payment_config, trial_enabled: v } }))}
                      className="data-[state=checked]:bg-emerald-500" />
                  </div>
                  <div className="p-3 rounded-xl border-2 border-emerald-200 bg-emerald-50/40">
                    <Label className="text-xs font-body text-emerald-800 font-semibold">Free access until — the ONE date (single source of truth)</Label>
                    <Input data-testid="trial-end-date-input" type="date"
                      value={settings.payment_config.trial_end_date || ''}
                      onChange={(e) => setSettings(s => ({ ...s, payment_config: { ...s.payment_config, trial_end_date: e.target.value, paywall_start_date: e.target.value } }))}
                      className="rounded-lg font-body text-sm mt-1 bg-white" />
                    <p className="text-[10px] text-emerald-700 font-body mt-1" data-testid="trial-date-echo">
                      Shows everywhere as <strong>{settings.payment_config.trial_end_date ? `${parseInt((settings.payment_config.trial_end_date.split('-')[2] || '0'), 10)} ${MONTHS[parseInt((settings.payment_config.trial_end_date.split('-')[1] || '1'), 10) - 1]} ${settings.payment_config.trial_end_date.split('-')[0]}` : '—'}</strong>. Paywall enforcement auto-mirrors this exact date.
                    </p>
                  </div>
                  <div className="p-3 rounded-xl border border-slate-200">
                    <Label className="text-xs font-body text-slate-500">Trial privilege (plan every trial user gets)</Label>
                    <select data-testid="trial-tier-select"
                      value={settings.payment_config.trial_tier || 'elite'}
                      onChange={(e) => setSettings(s => ({ ...s, payment_config: { ...s.payment_config, trial_tier: e.target.value } }))}
                      className="w-full p-2 rounded-lg border border-slate-200 text-sm font-body mt-1">
                      <option value="elite">Elite (highest — recommended)</option>
                      <option value="premium">Premium</option>
                      <option value="growth">Growth</option>
                    </select>
                    <p className="text-[10px] text-slate-400 font-body mt-1">Higher privilege = happier students, higher AI cost per user.</p>
                  </div>
                  <div className="p-3 rounded-xl border border-slate-200 grid grid-cols-[1fr_auto] gap-3 items-end">
                    <div>
                      <Label className="text-xs font-body text-slate-500">New-signup trial length (days)</Label>
                      <Input data-testid="signup-trial-days-input" type="number" min="0" max="365"
                        value={settings.payment_config.signup_trial_days ?? 14}
                        onChange={(e) => setSettings(s => ({ ...s, payment_config: { ...s.payment_config, signup_trial_days: parseInt(e.target.value) || 0 } }))}
                        className="rounded-lg font-body text-sm mt-1" />
                      <p className="text-[10px] text-slate-400 font-body mt-1">Applies after the festival window closes: each new user gets the trial plan for this many days from signup.</p>
                    </div>
                    <Switch data-testid="signup-trial-enabled-switch"
                      checked={settings.payment_config.signup_trial_enabled !== false}
                      onCheckedChange={(v) => setSettings(s => ({ ...s, payment_config: { ...s.payment_config, signup_trial_enabled: v } }))}
                      className="data-[state=checked]:bg-emerald-500 mb-2" />
                  </div>
                  <div className="md:col-span-2 p-3 rounded-xl border border-slate-200 grid grid-cols-[1fr_auto] gap-3 items-end">
                    <div>
                      <Label className="text-xs font-body text-slate-500">Trial banner copy — use <code className="bg-slate-100 px-1 rounded">{'{date}'}</code> and it auto-fills the date above, everywhere</Label>
                      <Input data-testid="trial-banner-input"
                        value={settings.payment_config.trial_banner_copy || ''}
                        onChange={(e) => setSettings(s => ({ ...s, payment_config: { ...s.payment_config, trial_banner_copy: e.target.value } }))}
                        className="rounded-lg font-body text-sm mt-1" />
                      <p className="text-[10px] text-slate-400 font-body mt-1">
                        Switch OFF to remove the whole “Free for every student until …” message from the landing page, login page and app header. Dates and paywall behaviour are untouched.
                      </p>
                    </div>
                    <Switch data-testid="trial-banner-enabled-switch"
                      checked={settings.payment_config.trial_banner_enabled !== false}
                      onCheckedChange={(v) => setSettings(s => ({ ...s, payment_config: { ...s.payment_config, trial_banner_enabled: v } }))}
                      className="data-[state=checked]:bg-emerald-500 mb-2" />
                  </div>
                  <div className="p-3 rounded-xl border border-slate-200">
                    <Label className="text-xs font-body text-slate-500">Paywall heading (after trial ends)</Label>
                    <Input data-testid="paywall-heading-input"
                      value={settings.payment_config.paywall_heading || ''}
                      onChange={(e) => setSettings(s => ({ ...s, payment_config: { ...s.payment_config, paywall_heading: e.target.value } }))}
                      className="rounded-lg font-body text-sm mt-1" />
                  </div>
                  <div className="p-3 rounded-xl border border-slate-200">
                    <Label className="text-xs font-body text-slate-500">Paywall subtitle</Label>
                    <Input data-testid="paywall-subtitle-input"
                      value={settings.payment_config.paywall_subtitle || ''}
                      onChange={(e) => setSettings(s => ({ ...s, payment_config: { ...s.payment_config, paywall_subtitle: e.target.value } }))}
                      className="rounded-lg font-body text-sm mt-1" />
                  </div>

                  <div className="md:col-span-2 mt-1 p-3 rounded-xl border border-dashed border-slate-300 bg-slate-50">
                    <p className="text-[11px] text-slate-500 font-body">
                      💳 Razorpay payment &amp; subscription links (Elite Lifetime Offer, Growth, Premium) now live inside
                      <strong> Subscription Settings</strong> — each one sits under its own plan so pricing and links stay together.
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 font-body mt-3">
                  💡 Razorpay keys live in <code className="px-1 py-0.5 rounded bg-slate-100">backend/.env</code> as
                  <code className="px-1 py-0.5 rounded bg-slate-100 ml-1">RAZORPAY_KEY_ID / RAZORPAY_KEY_SECRET / RAZORPAY_WEBHOOK_SECRET</code>.
                  Until they are set, Subscription page shows "Payments not configured" instead of opening checkout.
                </p>
              </Card>
            )}


            {/* Gamify Config (Phase 2.27) */}
            {showSetting('coins') && settings && (
              <Card className="p-6 rounded-2xl border-slate-100" data-testid="gamify-config-card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-base font-semibold text-slate-800 flex items-center gap-2">
                      <span className="text-violet-500">🧩</span> Gamify — puzzle economy
                    </h3>
                    <p className="text-xs text-slate-500 font-body">Per-tier puzzle caps, hint Veda Coin cost, and Daily Challenge settings.</p>
                  </div>
                  <Button data-testid="save-gamify-config-btn" size="sm"
                    onClick={async () => {
                      try {
                        await apiPut('/admin/settings', { gamify_config: settings.gamify_config });
                        toast.success('Gamify config saved');
                      } catch { toast.error('Failed to save'); }
                    }}
                    className="bg-violet-500 hover:bg-violet-600 text-white rounded-full font-heading text-xs">
                    Save Gamify Config
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {['basic','growth','premium','elite'].map(tier => (
                    <div key={tier} className="p-3 rounded-xl border border-slate-200">
                      <Label className="text-xs font-body text-slate-500 capitalize">
                        {tier.replace('_', ' ')} — unique puzzles per topic
                      </Label>
                      <Input data-testid={`gamify-cap-${tier}`} type="number" min="0" max="999"
                        value={settings.gamify_config?.tier_caps?.[tier] ?? 0}
                        onChange={e => setSettings(s => ({
                          ...s, gamify_config: {
                            ...(s.gamify_config || {}),
                            tier_caps: { ...(s.gamify_config?.tier_caps || {}), [tier]: parseInt(e.target.value) || 0 }
                          }
                        }))}
                        className="rounded-lg font-body text-sm mt-1" />
                      <p className="text-[10px] text-slate-400 font-body mt-1">
                        {tier === 'basic' ? '0 = locked (upgrade modal shown)' : 'Max unique LLM-generated puzzles per (subject, topic, difficulty).'}
                      </p>
                    </div>
                  ))}
                  <div className="p-3 rounded-xl border border-slate-200">
                    <Label className="text-xs font-body text-slate-500">Hint Veda Coin cost</Label>
                    <Input data-testid="gamify-hint-cost" type="number" min="0"
                      value={settings.gamify_config?.hint_cost_xp ?? 5}
                      onChange={e => setSettings(s => ({
                        ...s, gamify_config: { ...(s.gamify_config || {}), hint_cost_xp: parseInt(e.target.value) || 0 }
                      }))}
                      className="rounded-lg font-body text-sm mt-1" />
                    <p className="text-[10px] text-slate-400 font-body mt-1">Veda Coins deducted when a kid uses a hint. 0 = free hints.</p>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <h4 className="font-heading text-sm text-slate-700 mb-3">Daily Challenge</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
                      <div>
                        <Label className="text-sm font-heading text-slate-700">Enabled</Label>
                        <p className="text-[11px] text-slate-400 font-body">Shows the ribbon on /gamify for all tiers.</p>
                      </div>
                      <Switch data-testid="gamify-daily-enabled-switch"
                        checked={!!settings.gamify_config?.daily_challenge_enabled}
                        onCheckedChange={(v) => setSettings(s => ({
                          ...s, gamify_config: { ...(s.gamify_config || {}), daily_challenge_enabled: v }
                        }))}
                        className="data-[state=checked]:bg-violet-500" />
                    </div>
                    <div className="p-3 rounded-xl border border-slate-200">
                      <Label className="text-xs font-body text-slate-500">Bonus Veda Coin multiplier</Label>
                      <Input data-testid="gamify-daily-multiplier" type="number" step="0.1" min="1"
                        value={settings.gamify_config?.daily_challenge_bonus_multiplier ?? 3}
                        onChange={e => setSettings(s => ({
                          ...s, gamify_config: { ...(s.gamify_config || {}), daily_challenge_bonus_multiplier: parseFloat(e.target.value) || 1 }
                        }))}
                        className="rounded-lg font-body text-sm mt-1" />
                      <p className="text-[10px] text-slate-400 font-body mt-1">Base Veda Coins × this = daily challenge reward.</p>
                    </div>
                    <div className="md:col-span-2 p-3 rounded-xl border border-slate-200">
                      <Label className="text-xs font-body text-slate-500">Subject rotation (comma-separated)</Label>
                      <Input data-testid="gamify-daily-rotation"
                        value={(settings.gamify_config?.daily_challenge_subject_rotation || []).join(', ')}
                        onChange={e => setSettings(s => ({
                          ...s, gamify_config: {
                            ...(s.gamify_config || {}),
                            daily_challenge_subject_rotation: e.target.value.split(',').map(x => x.trim()).filter(Boolean)
                          }
                        }))}
                        className="rounded-lg font-body text-sm mt-1" />
                      <p className="text-[10px] text-slate-400 font-body mt-1">
                        Each day picks the next subject in this list. Valid: math, physics, chemistry, biology, english, hindi, social_studies, logic.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pre-seed puzzles */}
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <h4 className="font-heading text-sm text-slate-700 mb-2">Pre-seed puzzles (background job)</h4>
                  <p className="text-[11px] text-slate-400 font-body mb-3">
                    Warm the cache so Premium/Elite kids never wait on the LLM. Leave fields blank to seed EVERYTHING (expensive — kicks off ~500 LLM calls). Safer: seed one (subject, class, difficulty) at a time.
                  </p>
                  <PuzzleSeedForm />
                </div>
              </Card>
            )}


            {/* Board Visibility — belongs to Content & Curriculum, nowhere else */}
            {showSetting('content') && (
              <Card className="p-6 rounded-2xl border-slate-100">
                <div className="flex items-center gap-2 mb-2"><GraduationCap className="w-5 h-5 text-blue-500" /><h3 className="font-heading text-base font-semibold text-slate-800">Education Board Visibility</h3></div>
                <p className="text-xs text-slate-500 font-body mb-5">Toggle which boards students see during onboarding. ({boards.length} boards)</p>
                {['National', 'International', 'State'].map(category => {
                  const categoryBoards = boards.filter(b => b.category === category);
                  if (categoryBoards.length === 0) return null;
                  return (
                    <div key={category} className="mb-5">
                      <h4 className="font-heading text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{category} Boards ({categoryBoards.length})</h4>
                      <div className="space-y-1 border border-slate-100 rounded-xl overflow-hidden">
                        {categoryBoards.map(b => (
                          <div key={b.board_id} className="flex items-center justify-between py-2.5 px-4 hover:bg-slate-50 transition-colors" data-testid={`board-toggle-${b.board_id}`}>
                            <p className="font-heading text-sm font-medium text-slate-700">{b.name}</p>
                            <Switch data-testid={`board-switch-${b.board_id}`} checked={b.visible ?? false} onCheckedChange={() => toggleBoardVisibility(b.board_id)} className="data-[state=checked]:bg-blue-500" />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </Card>
            )}
            {showSetting('content') && <GradeVisibilityCard boards={boards} setBoards={setBoards} />}
          </TabsContent>

          {/* ==================== SUBSCRIPTION SETTINGS TAB (Phase 2.41) ==================== */}
          <TabsContent value="pricing" className="space-y-6" data-testid="pricing-tab">
            {/* Subscription Plans — the tier editor, home of Subscription Settings */}
            {subTiers && (
              <Card className="p-6 rounded-2xl border-slate-100" data-testid="subscription-management">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-base font-semibold text-slate-800">Subscription Plans</h3>
                    <p className="text-xs text-slate-500 font-body">Edit pricing, features, status (Visible / Coming Soon / Hidden) and limits per tier.</p>
                  </div>
                  <Button data-testid="save-tiers-btn" onClick={saveTiers} disabled={savingTiers} size="sm"
                    className="bg-amber-500 hover:bg-amber-600 text-white rounded-full font-heading text-xs">
                    {savingTiers ? 'Saving...' : 'Save Tiers'}
                  </Button>
                </div>
                <div className="space-y-6">
                  {Object.entries(subTiers).map(([tierKey, tier]) => {
                    const status = tier.tier_status || (tier.visible === false ? 'hidden' : 'visible');
                    const statusBg = status === 'hidden' ? 'border-red-200 bg-red-50/30 opacity-75' : status === 'coming_soon' ? 'border-violet-200 bg-violet-50/30' : 'border-slate-200';
                    return (
                    <div key={tierKey} className={`border rounded-xl p-4 ${statusBg}`} data-testid={`tier-edit-${tierKey}`}>
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <Badge className={tierKey === 'basic' ? 'bg-slate-100 text-slate-600' : tierKey === 'growth' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}>
                          {tier.name}
                        </Badge>
                        {/* 3-state segmented control */}
                        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-[11px] font-heading">
                          {[
                            { v: 'visible', label: 'Visible', cls: 'data-[active=true]:bg-emerald-500 data-[active=true]:text-white' },
                            { v: 'coming_soon', label: 'Coming Soon', cls: 'data-[active=true]:bg-violet-500 data-[active=true]:text-white' },
                            { v: 'hidden', label: 'Hidden', cls: 'data-[active=true]:bg-slate-700 data-[active=true]:text-white' },
                          ].map(opt => (
                            <button key={opt.v} type="button"
                              data-testid={`tier-status-${tierKey}-${opt.v}`} data-active={status === opt.v}
                              onClick={() => {
                                updateTierField(tierKey, 'tier_status', opt.v);
                                updateTierField(tierKey, 'visible', opt.v !== 'hidden');
                              }}
                              className={`px-2.5 py-1 transition-colors ${status === opt.v ? (opt.v === 'visible' ? 'bg-emerald-500 text-white' : opt.v === 'coming_soon' ? 'bg-violet-500 text-white' : 'bg-slate-700 text-white') : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 ml-auto">
                          {/* Billing cycle selector — only relevant for paid
                              tiers (Growth / Elite / paid Basic Lifetime).
                              Drives the price-label suffix on the Subscription
                              page; Phase 2 will hook this into recurring
                              Razorpay subscriptions vs. one-time orders. */}
                          {tierKey !== 'basic' && (
                            <>
                              <Label className="text-xs font-body text-slate-500">Billing</Label>
                              <select
                                data-testid={`tier-billing-cycle-${tierKey}`}
                                value={tier.billing_cycle || 'monthly'}
                                onChange={e => updateTierField(tierKey, 'billing_cycle', e.target.value)}
                                className="w-32 p-1.5 rounded-lg border border-slate-200 text-xs font-body bg-white">
                                <option value="monthly">Per month</option>
                                <option value="yearly">Per year</option>
                                <option value="lifetime">Lifetime (one-time)</option>
                              </select>
                            </>
                          )}
                          <Label className="text-xs font-body text-slate-500">Price (Rs)</Label>
                          <Input data-testid={`tier-price-${tierKey}`} type="number" value={tier.price || 0}
                            onChange={e => updateTierField(tierKey, 'price', parseInt(e.target.value) || 0)}
                            className="w-24 rounded-lg font-body text-sm h-8" />
                        </div>
                      </div>

                      {/* Limits */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                        {[
                          { key: 'chat_messages_per_day', label: 'Msgs/day' },
                          { key: 'quiz_per_day', label: 'Quiz/day' },
                          { key: 'flashcard_sets_per_day', label: 'Flash/day' },
                          { key: 'snap_solve_per_day', label: 'Snap/day' },
                        ].map(l => (
                          <div key={l.key}>
                            <Label className="text-[10px] font-body text-slate-400">{l.label}</Label>
                            <select data-testid={`tier-limit-${tierKey}-${l.key}`}
                              value={(tier.limits?.[l.key] || 0) >= 9999 ? 'unlimited' : tier.limits?.[l.key] || 0}
                              onChange={e => updateTierLimit(tierKey, l.key, e.target.value)}
                              className="w-full p-1.5 rounded-lg border border-slate-200 text-xs font-body mt-0.5">
                              {[5, 10, 15, 20, 25, 50, 100, 200, 500].map(v => (
                                <option key={v} value={v}>{v}</option>
                              ))}
                              <option value="unlimited">Unlimited</option>
                            </select>
                          </div>
                        ))}
                      </div>

                      {/* Features list */}
                      <div className="space-y-1.5">
                        <Label className="text-[10px] font-body text-slate-400 uppercase tracking-wider">Features</Label>
                        {(tier.features || []).map((f, i) => (
                          <div key={i} className="flex items-center gap-1.5">
                            <Input value={f} onChange={e => updateTierFeature(tierKey, i, e.target.value)}
                              className="flex-1 rounded-lg font-body text-xs h-7" placeholder="Feature description" />
                            <button onClick={() => removeTierFeature(tierKey, i)} className="text-red-400 hover:text-red-600 text-xs px-1">x</button>
                          </div>
                        ))}
                        <button onClick={() => addTierFeature(tierKey)}
                          className="text-xs text-blue-500 font-body hover:underline">+ Add feature</button>
                      </div>
                    </div>
                  );})}
                </div>
              </Card>
            )}
            <Card className="p-6 rounded-2xl border-slate-100">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <h3 className="font-heading text-base font-semibold text-slate-800 flex items-center gap-2">
                    <CircleDollarSign className="w-5 h-5 text-emerald-600" />
                    Subscription Settings — Hybrid Pricing
                  </h3>
                  <p className="text-xs text-slate-500 font-body mt-1">
                    Offer multiple billing options per tier simultaneously. Toggle any combination —
                    monthly subscription, yearly one-time, or lifetime one-time — and students choose
                    on the Subscription page. Disabled plans are hidden from end users.
                  </p>
                </div>
                <Button data-testid="save-pricing-btn" onClick={saveTiers} disabled={savingTiers} size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-heading text-xs">
                  {savingTiers ? 'Saving…' : 'Save pricing'}
                </Button>
              </div>
              {subTiers && Object.entries(subTiers).filter(([k]) => k !== 'basic').map(([tierKey, tier]) => {
                const accentClr = tierKey === 'growth' ? 'text-blue-700' : 'text-amber-700';
                const accentBg = tierKey === 'growth' ? 'bg-blue-50' : 'bg-amber-50';
                const planRows = [
                  { key: 'monthly',  label: 'Monthly Subscription', hint: 'Recurring · students billed every month',         color: 'border-blue-100  bg-blue-50/30' },
                  { key: 'yearly',   label: 'Annual One-Time',      hint: 'One payment — covers 12 months of access',       color: 'border-violet-100 bg-violet-50/30' },
                  { key: 'half_yearly', label: '6-Month One-Time',  hint: 'Launch offer · auto-renews as Annual on the date below', color: 'border-amber-100 bg-amber-50/30' },
                  { key: 'lifetime', label: 'Lifetime One-Time',    hint: 'Pay once · access forever (highest LTV revenue)', color: 'border-emerald-100 bg-emerald-50/30' },
                ];
                return (
                  <div key={tierKey} className="mt-6 border border-slate-200 rounded-2xl p-5" data-testid={`pricing-tier-${tierKey}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <Badge className={`${accentBg} ${accentClr} font-heading text-xs`}>{tier.name}</Badge>
                      <span className="text-xs text-slate-500 font-body">
                        Pick which plans students can buy. At least one must stay enabled.
                      </span>
                    </div>
                    <div className="space-y-3">
                      {planRows.map(pr => {
                        const plan = (tier.billing_plans || {})[pr.key] || { enabled: false, price: 0 };
                        return (
                          <div key={pr.key}
                            className={`flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl border ${pr.color}`}
                            data-testid={`pricing-plan-${tierKey}-${pr.key}`}>
                            <div className="flex items-center gap-2">
                              <Switch
                                data-testid={`pricing-toggle-${tierKey}-${pr.key}`}
                                checked={!!plan.enabled}
                                onCheckedChange={(v) => togglePlanEnabled(tierKey, pr.key, v)}
                                className="data-[state=checked]:bg-emerald-500"
                              />
                              <div className="min-w-[12rem]">
                                <p className="text-sm font-heading text-slate-800">{pr.label}</p>
                                <p className="text-[11px] text-slate-500 font-body">{pr.hint}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-auto">
                              <Label className="text-xs font-body text-slate-500">Price (Rs)</Label>
                              <Input
                                data-testid={`pricing-price-${tierKey}-${pr.key}`}
                                type="number"
                                value={plan.price || 0}
                                onChange={e => updatePlanPrice(tierKey, pr.key, e.target.value)}
                                disabled={!plan.enabled}
                                className="w-28 rounded-lg font-body text-sm h-8 disabled:bg-slate-100 disabled:opacity-60"
                              />
                              <span className="text-xs text-slate-400 font-body min-w-[5.5rem]">
                                {plan.enabled
                                  ? `Rs ${(plan.price || 0).toLocaleString('en-IN')} ${(plan.label_suffix || '').trim()}`
                                  : 'Hidden'}
                              </span>
                            </div>
                            <div className="w-full flex flex-wrap items-center gap-2 pl-1">
                              <Label className="text-[11px] font-body text-slate-500 min-w-[9rem]">Razorpay link (optional)</Label>
                              <Input
                                data-testid={`pricing-url-${tierKey}-${pr.key}`}
                                value={plan.payment_url || ''}
                                placeholder="https://rzp.io/rzp/xxxxxxx"
                                onChange={e => updatePlanField(tierKey, pr.key, 'payment_url', e.target.value)}
                                disabled={!plan.enabled}
                                className="flex-1 min-w-[14rem] rounded-lg font-body text-xs h-8 disabled:bg-slate-100 disabled:opacity-60"
                              />
                              {pr.key === 'half_yearly' && (
                                <>
                                  <Label className="text-[11px] font-body text-slate-500">Auto-renews as Annual on</Label>
                                  <Input
                                    data-testid={`pricing-renew-on-${tierKey}`}
                                    type="date"
                                    value={plan.renew_on || ''}
                                    onChange={e => updatePlanField(tierKey, pr.key, 'renew_on', e.target.value)}
                                    disabled={!plan.enabled}
                                    className="w-40 rounded-lg font-body text-xs h-8 disabled:bg-slate-100 disabled:opacity-60"
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {renderTierPaymentLink(tierKey)}
                  </div>
                );
              })}
              <p className="text-[11px] text-slate-400 font-body mt-4">
                💡 Once Phase 2 (Razorpay Recurring) ships, monthly plans automatically switch to
                Razorpay Subscriptions; yearly and lifetime stay as one-time orders. No code change
                needed — just flip the appropriate toggles in this panel.
              </p>
            </Card>
          </TabsContent>



          {/* ==================== COST & USAGE TAB ==================== */}
          <TabsContent value="cost" className="space-y-6" data-testid="cost-tab">
            <CostDashboard />
          </TabsContent>

          {/* ==================== FINANCE TAB ==================== */}
          <TabsContent value="finance" className="space-y-6" data-testid="finance-tab">
            <FinanceDashboard />
          </TabsContent>

          <TabsContent value="referrals" className="space-y-6" data-testid="referrals-tab">
            <ReferralDashboard />
            {/* Refer & Earn — lives in the Referrals tab (its natural home) */}
            {settings?.referral_config && (
              <Card className="p-6 rounded-2xl border-slate-100" data-testid="referral-config-card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-base font-semibold text-slate-800 flex items-center gap-2">
                      <span className="text-emerald-500">🎁</span> Refer &amp; Earn — rewards engine
                    </h3>
                    <p className="text-xs text-slate-500 font-body">Tweak point values, qualifying days, and redemption ratio as the market shifts.</p>
                  </div>
                  <Button data-testid="save-referral-config-btn" size="sm"
                    onClick={async () => {
                      try {
                        await apiPut('/admin/settings', { referral_config: settings.referral_config });
                        toast.success('Referral rewards saved');
                      } catch { toast.error('Failed to save'); }
                    }}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-heading text-xs">
                    Save Referral Rules
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
                    <div>
                      <Label className="text-sm font-heading text-slate-700">Program enabled</Label>
                      <p className="text-[11px] text-slate-400 font-body">Hides the header pill &amp; /refer page when off.</p>
                    </div>
                    <Switch data-testid="referral-enabled-switch"
                      checked={!!settings.referral_config.enabled}
                      onCheckedChange={(v) => setSettings(s => ({ ...s, referral_config: { ...s.referral_config, enabled: v } }))}
                      className="data-[state=checked]:bg-emerald-500" />
                  </div>
                  <div className="p-3 rounded-xl border border-slate-200">
                    <Label className="text-xs font-body text-slate-500">Redemption target plan</Label>
                    <select data-testid="referral-redeem-plan"
                      value={settings.referral_config.redeem_plan || 'growth'}
                      onChange={e => setSettings(s => ({ ...s, referral_config: { ...s.referral_config, redeem_plan: e.target.value } }))}
                      className="w-full p-2 rounded-lg border border-slate-200 text-sm font-body mt-1">
                      <option value="growth">Growth</option>
                      <option value="premium">Premium</option>
                      <option value="elite">Elite</option>
                    </select>
                  </div>
                  <div className="p-3 rounded-xl border border-slate-200">
                    <Label className="text-xs font-body text-slate-500">Points when referee active for N days</Label>
                    <Input data-testid="referral-points-active" type="number" min="0"
                      value={settings.referral_config.points_active_referee ?? 5}
                      onChange={e => setSettings(s => ({ ...s, referral_config: { ...s.referral_config, points_active_referee: parseInt(e.target.value) || 0 } }))}
                      className="rounded-lg font-body text-sm mt-1" />
                  </div>
                  <div className="p-3 rounded-xl border border-slate-200">
                    <Label className="text-xs font-body text-slate-500">Points when referee upgrades to paid</Label>
                    <Input data-testid="referral-points-paid" type="number" min="0"
                      value={settings.referral_config.points_paid_referee ?? 10}
                      onChange={e => setSettings(s => ({ ...s, referral_config: { ...s.referral_config, points_paid_referee: parseInt(e.target.value) || 0 } }))}
                      className="rounded-lg font-body text-sm mt-1" />
                  </div>
                  <div className="p-3 rounded-xl border border-slate-200">
                    <Label className="text-xs font-body text-slate-500">Qualifying active days</Label>
                    <Input data-testid="referral-qualify-days" type="number" min="0"
                      value={settings.referral_config.qualify_active_days ?? 7}
                      onChange={e => setSettings(s => ({ ...s, referral_config: { ...s.referral_config, qualify_active_days: parseInt(e.target.value) || 0 } }))}
                      className="rounded-lg font-body text-sm mt-1" />
                  </div>
                  <div className="p-3 rounded-xl border border-slate-200">
                    <Label className="text-xs font-body text-slate-500">Points per free month</Label>
                    <Input data-testid="referral-points-redeem" type="number" min="1"
                      value={settings.referral_config.points_per_free_month ?? 50}
                      onChange={e => setSettings(s => ({ ...s, referral_config: { ...s.referral_config, points_per_free_month: parseInt(e.target.value) || 1 } }))}
                      className="rounded-lg font-body text-sm mt-1" />
                  </div>
                  <div className="p-3 rounded-xl border border-slate-200">
                    <Label className="text-xs font-body text-slate-500">Days added per redemption</Label>
                    <Input data-testid="referral-days-redeem" type="number" min="1"
                      value={settings.referral_config.days_per_redemption ?? 30}
                      onChange={e => setSettings(s => ({ ...s, referral_config: { ...s.referral_config, days_per_redemption: parseInt(e.target.value) || 1 } }))}
                      className="rounded-lg font-body text-sm mt-1" />
                  </div>
                  <div className="md:col-span-2 p-3 rounded-xl border border-slate-200">
                    <Label className="text-xs font-body text-slate-500">Promo headline (shown on /refer)</Label>
                    <Input data-testid="referral-promo-headline"
                      value={settings.referral_config.promo_headline || ''}
                      onChange={e => setSettings(s => ({ ...s, referral_config: { ...s.referral_config, promo_headline: e.target.value } }))}
                      className="rounded-lg font-body text-sm mt-1" />
                  </div>
                  <div className="md:col-span-2 p-3 rounded-xl border border-slate-200">
                    <Label className="text-xs font-body text-slate-500">Share-message template (used in WhatsApp/Email/Twitter)</Label>
                    <Input data-testid="referral-share-message"
                      value={settings.referral_config.promo_share_message || ''}
                      onChange={e => setSettings(s => ({ ...s, referral_config: { ...s.referral_config, promo_share_message: e.target.value } }))}
                      className="rounded-lg font-body text-sm mt-1" />
                    <p className="text-[10px] text-slate-400 font-body mt-1">The user's invite link is auto-appended at the end.</p>
                  </div>
                </div>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="schools" className="space-y-6" data-testid="schools-tab">
            <CustomSchoolsReport />
          </TabsContent>

          <TabsContent value="website-ads" className="space-y-6" data-testid="website-ads-tab">
            <WebsiteAdsManager />
          </TabsContent>

          {/* ==================== EMOTIONAL SAFETY TAB ==================== */}
          <TabsContent value="safety" className="space-y-6" data-testid="safety-tab">
            <EmotionalSafetyDashboard />
          </TabsContent>

          <TabsContent value="feedback" className="space-y-6" data-testid="feedback-tab">
            <FeedbackDashboard />
          </TabsContent>

          {/* ==================== LEGAL TAB ==================== */}
          <TabsContent value="legal" className="space-y-6">
            {/* Business Details Card */}
            <Card className="p-6 rounded-2xl border-slate-100" data-testid="legal-info-card">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-5 h-5 text-indigo-500" />
                <h3 className="font-heading text-base font-semibold text-slate-800">Business details</h3>
              </div>
              <p className="text-xs text-slate-500 font-body mb-5">
                Shown on every legal page and used by the payment gateway for approval. Keep this up to date — these fields are auto-substituted into Terms, Privacy, Refund, Shipping, and Contact pages.
              </p>
              {settings?.legal_info && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'company_name', label: 'Legal company name' },
                    { key: 'brand_name', label: 'Brand / trading name' },
                    { key: 'address_line1', label: 'Address line 1', full: true },
                    { key: 'address_line2', label: 'Address line 2', full: true },
                    { key: 'city', label: 'City' },
                    { key: 'state', label: 'State' },
                    { key: 'pincode', label: 'PIN code' },
                    { key: 'country', label: 'Country' },
                    { key: 'support_email', label: 'Support email' },
                    { key: 'support_phone', label: 'Support phone' },
                    { key: 'gstin', label: 'GSTIN (optional)' },
                    { key: 'website', label: 'Website URL' },
                    { key: 'effective_date', label: 'Effective date (YYYY-MM-DD)' },
                  ].map(f => (
                    <div key={f.key} className={f.full ? 'sm:col-span-2' : ''} data-testid={`legal-info-field-${f.key}`}>
                      <Label className="text-xs font-body text-slate-600">{f.label}</Label>
                      <Input
                        data-testid={`legal-info-input-${f.key}`}
                        value={settings.legal_info[f.key] ?? ''}
                        onChange={e => updateLegalInfo(f.key, e.target.value)}
                        className="mt-1 rounded-xl font-body" />
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-slate-400 font-body italic mt-4">
                Placeholders like <code className="bg-slate-100 px-1 rounded">{'{{company_name}}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{{support_email}}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{{full_address}}'}</code> are auto-replaced inside the legal page bodies using these values.
              </p>
            </Card>

            {/* Legal Pages Editor */}
            <Card className="p-6 rounded-2xl border-slate-100" data-testid="legal-pages-card">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-500" />
                  <div>
                    <h3 className="font-heading text-base font-semibold text-slate-800">Legal page content</h3>
                    <p className="text-xs text-slate-500 font-body">
                      Markdown-supported. <a href={`/${legalActiveTab}`} target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">Preview the live page →</a>
                    </p>
                  </div>
                </div>
                <Button data-testid="save-legal-btn" onClick={saveLegal} disabled={savingLegal}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-full font-heading text-xs">
                  {savingLegal ? 'Saving...' : 'Save Legal Content'}
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-1 mb-4 border-b border-slate-100">
                {[
                  { slug: 'terms', label: 'Terms' },
                  { slug: 'privacy', label: 'Privacy' },
                  { slug: 'refund', label: 'Refund' },
                  { slug: 'shipping', label: 'Shipping' },
                  { slug: 'contact', label: 'Contact' },
                ].map(t => (
                  <button key={t.slug} data-testid={`legal-tab-${t.slug}`}
                    onClick={() => setLegalActiveTab(t.slug)}
                    className={`px-4 py-2 text-xs font-heading rounded-t-lg transition-all border-b-2 ${legalActiveTab === t.slug ? 'text-indigo-600 border-indigo-500 bg-indigo-50/40' : 'text-slate-500 border-transparent hover:text-slate-800'}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              {settings?.legal_pages?.[legalActiveTab] && (
                <div className="space-y-3" data-testid={`legal-editor-${legalActiveTab}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-body text-slate-600">Page title</Label>
                      <Input
                        data-testid={`legal-title-input-${legalActiveTab}`}
                        value={settings.legal_pages[legalActiveTab].title ?? ''}
                        onChange={e => updateLegalPage(legalActiveTab, 'title', e.target.value)}
                        className="mt-1 rounded-xl font-body" />
                    </div>
                    <div>
                      <Label className="text-xs font-body text-slate-600">Last updated (display)</Label>
                      <Input
                        data-testid={`legal-updated-input-${legalActiveTab}`}
                        value={settings.legal_pages[legalActiveTab].updated ?? ''}
                        onChange={e => updateLegalPage(legalActiveTab, 'updated', e.target.value)}
                        placeholder="e.g. 21 April 2026"
                        className="mt-1 rounded-xl font-body" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-body text-slate-600">Body (Markdown)</Label>
                    <textarea
                      data-testid={`legal-body-input-${legalActiveTab}`}
                      value={settings.legal_pages[legalActiveTab].body ?? ''}
                      onChange={e => updateLegalPage(legalActiveTab, 'body', e.target.value)}
                      rows={22}
                      className="mt-1 w-full rounded-xl font-mono text-xs text-slate-700 bg-slate-50 border border-slate-200 px-4 py-3 leading-relaxed focus:outline-none focus:border-indigo-300"
                      spellCheck={false} />
                    <div className="flex items-center justify-between flex-wrap gap-2 mt-2">
                      <p className="text-[11px] text-slate-400 font-body italic">
                        Supports <code className="bg-slate-100 px-1 rounded">## Heading</code>, <code className="bg-slate-100 px-1 rounded">### Sub</code>, <code className="bg-slate-100 px-1 rounded">**bold**</code>, <code className="bg-slate-100 px-1 rounded">[link](/url)</code>, bullet lists with <code className="bg-slate-100 px-1 rounded">-</code>.
                      </p>
                      <button onClick={() => resetLegalPage(legalActiveTab)}
                        data-testid={`legal-reset-${legalActiveTab}`}
                        className="text-[11px] text-rose-500 hover:underline">
                        Reset to default
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* ==================== AUDIT LOG TAB ==================== */}
          <TabsContent value="audit" className="space-y-6">
            <Card className="rounded-2xl border-slate-100 overflow-hidden" data-testid="audit-log-card">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-heading text-base font-semibold text-slate-800">Delete Audit Log</h3>
                  <p className="text-xs text-slate-500 font-body mt-0.5">Every deletion (content + users) tracked for accountability</p>
                </div>
                <div className="flex items-center gap-2">
                  <select data-testid="audit-filter" value={auditFilter} onChange={e => { setAuditFilter(e.target.value); loadAuditLog(e.target.value); }}
                    className="text-xs font-heading px-3 py-1.5 rounded-lg border border-slate-200 bg-white">
                    <option value="">All deletions</option>
                    <option value="content">Content only</option>
                    <option value="user">Users only</option>
                  </select>
                  <Button data-testid="audit-refresh" variant="outline" size="sm" onClick={() => loadAuditLog(auditFilter)}
                    className="rounded-lg font-heading text-xs">Refresh</Button>
                </div>
              </div>
              {auditLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : auditLog.length === 0 ? (
                <div className="p-12 text-center text-slate-400 text-sm font-body" data-testid="audit-empty">
                  <History className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                  No deletions recorded yet. Every delete will appear here.
                </div>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="font-heading text-xs">When</TableHead>
                    <TableHead className="font-heading text-xs">Actor</TableHead>
                    <TableHead className="font-heading text-xs">Action</TableHead>
                    <TableHead className="font-heading text-xs">Target</TableHead>
                    <TableHead className="font-heading text-xs">Details</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {auditLog.map(a => {
                      const s = a.target_snapshot || {};
                      const m = a.metadata || {};
                      const when = a.created_at ? new Date(a.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';
                      const actionLabel = a.action === 'content_single' ? 'Content · Single' : a.action === 'content_bulk' ? 'Content · Bulk' : a.action === 'user_delete' ? 'User' : a.action;
                      const actionColor = a.action === 'user_delete' ? 'bg-rose-50 text-rose-700 border-rose-100' : a.action === 'content_bulk' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-slate-50 text-slate-700 border-slate-100';
                      return (
                        <TableRow key={a.audit_id} data-testid={`audit-row-${a.audit_id}`}>
                          <TableCell className="font-body text-xs text-slate-500 whitespace-nowrap">{when}</TableCell>
                          <TableCell className="font-body text-xs">
                            <p className="font-heading text-slate-700">{a.actor_name || 'System'}</p>
                            <p className="text-[10px] text-slate-400">{a.actor_email || a.actor_role}</p>
                          </TableCell>
                          <TableCell>
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-heading border ${actionColor}`}>{actionLabel}</span>
                          </TableCell>
                          <TableCell className="font-body text-xs">
                            {a.target_type === 'user' ? (
                              <div>
                                <p className="font-heading text-slate-700">{s.name || s.email || a.target_id}</p>
                                <p className="text-[10px] text-slate-400">{s.role} · {s.email}</p>
                              </div>
                            ) : (
                              <div>
                                <p className="font-heading text-slate-700 line-clamp-1">
                                  {s.title || (s.sample_titles ? s.sample_titles.slice(0,2).join(' · ') + (s.sample_titles.length > 2 ? ' …' : '') : a.target_id)}
                                </p>
                                <p className="text-[10px] text-slate-400">
                                  {s.subject ? `${s.subject} · ${s.topic || ''}` : (s.filter ? 'Bulk filter' : a.target_type)}
                                </p>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="font-body text-[11px] text-slate-500">
                            {a.action === 'content_bulk' && <span>{m.deleted_count || 0} items · {m.files_deleted || 0} files</span>}
                            {a.action === 'user_delete' && m.cascades && (
                              <span className="text-slate-500">
                                {Object.entries(m.cascades).filter(([, v]) => (v || 0) > 0).map(([k, v]) => `${k}:${v}`).join(' · ')}
                              </span>
                            )}
                            {a.action === 'content_single' && <span>{m.file_deleted ? 'file removed' : 'db only'}</span>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Confirmation modal — delete user */}
      {confirmDeleteUser && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="delete-user-modal"
          onClick={() => setConfirmDeleteUser(null)}>
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Delete user permanently?</h3>
            <p className="text-sm text-slate-500 font-body mb-2">
              <span className="font-semibold text-slate-700">{confirmDeleteUser.name}</span> ({confirmDeleteUser.email}) will be removed along with their chat history, progress, quizzes, flashcards, and voice usage. This cannot be undone.
            </p>
            <p className="text-[11px] text-slate-400 font-body italic mb-5">An audit entry will be created under the Audit Log tab.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDeleteUser(null)} data-testid="delete-user-cancel"
                className="px-4 py-2 rounded-full text-sm font-heading bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors">Cancel</button>
              <button data-testid="delete-user-confirm" onClick={() => handleDeleteUser(confirmDeleteUser)}
                className="px-4 py-2 rounded-full text-sm font-heading bg-red-500 text-white hover:bg-red-600 transition-colors">Delete User</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Signup trend bar chart
function SignupCard({ initialData }) {
  // Iter 60w (Aug 2026) — New Signups panel now supports:
  //   • 4 filter chips (7d / 30d / 90d / Custom)
  //   • Zero-filled daily bars (every day gets a bar, even 0)
  //   • Hover tooltip showing Date · New Signups · Cumulative Total
  const [mode, setMode] = React.useState('14');
  const [data, setData] = React.useState(initialData || []);
  const [customFrom, setCustomFrom] = React.useState('');
  const [customTo, setCustomTo] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (mode === 'custom' && (!customFrom || !customTo)) return;
    setLoading(true);
    const q = mode === 'custom'
      ? `signup_from=${customFrom}&signup_to=${customTo}`
      : `signup_days=${mode}`;
    apiGet(`/admin/stats?${q}`)
      .then(d => setData(d?.signup_trend || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [mode, customFrom, customTo]);

  const label = mode === 'custom'
    ? (customFrom && customTo ? `${customFrom} → ${customTo}` : 'Custom range')
    : `Last ${mode} days`;

  return (
    <Card className="p-5 rounded-2xl border-slate-100" data-testid="signup-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-800">New Signups</h3>
          <p className="text-xs font-body text-slate-500">{label}</p>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {[
            { k: '7', l: '7d' },
            { k: '30', l: '30d' },
            { k: '90', l: '90d' },
            { k: 'custom', l: 'Custom' },
          ].map(f => (
            <button
              key={f.k}
              onClick={() => setMode(f.k)}
              data-testid={`signup-filter-${f.k}`}
              className={`px-2.5 py-1 text-[11px] font-heading rounded-lg transition-colors ${
                mode === f.k
                  ? 'bg-emerald-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >{f.l}</button>
          ))}
        </div>
      </div>
      {mode === 'custom' && (
        <div className="flex items-center gap-2 mb-3">
          <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
            data-testid="signup-custom-from"
            className="text-xs font-body px-2 py-1 rounded-lg border border-slate-200 focus:border-emerald-400 focus:outline-none" />
          <span className="text-xs text-slate-400">to</span>
          <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
            data-testid="signup-custom-to"
            className="text-xs font-body px-2 py-1 rounded-lg border border-slate-200 focus:border-emerald-400 focus:outline-none" />
        </div>
      )}
      {loading ? (
        <div className="h-40 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <SignupChart data={data} />
      )}
    </Card>
  );
}

function SignupChart({ data }) {
  const [hover, setHover] = React.useState(null);
  if (!data || data.length === 0) {
    return <p className="text-sm text-slate-400 font-body text-center py-6">No signups yet in this range.</p>;
  }
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const w = 800, h = 200, padL = 34, padR = 12, padT = 12, padB = 34;
  const chartW = w - padL - padR, chartH = h - padT - padB;
  const gap = chartW / data.length;
  const barW = Math.max(gap * 0.55, 2);

  // Adaptive X-axis label density — with 90+ days we can't label every day.
  const labelEvery = data.length <= 14 ? 1
    : data.length <= 30 ? 3
    : data.length <= 60 ? 7
    : 14;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full mt-2" data-testid="signup-chart">
        {/* gridlines */}
        {Array.from({ length: Math.min(maxCount + 2, 6) }, (_, v) => v).map(v => {
          const y = padT + chartH - (v / (maxCount + 1)) * chartH;
          return (
            <g key={v}>
              <line x1={padL} y1={y} x2={w - padR} y2={y} stroke="#E2E8F0" strokeWidth="0.5" strokeDasharray="4,4" />
              <text x={padL - 6} y={y + 4} textAnchor="end" fill="#94A3B8" fontSize="10" fontFamily="DM Sans">{v}</text>
            </g>
          );
        })}
        {/* bars */}
        {data.map((d, i) => {
          const barH = Math.max((d.count / (maxCount + 1)) * chartH, d.count > 0 ? 4 : 0);
          const x = padL + i * gap + (gap - barW) / 2;
          const y = padT + chartH - barH;
          const showLabel = i % labelEvery === 0 || i === data.length - 1;
          const active = hover === i;
          return (
            <g key={i}>
              {/* full-height hit area so mousing anywhere in the column shows the tooltip — including on 0-count days */}
              <rect
                x={padL + i * gap} y={padT} width={gap} height={chartH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                data-testid={`signup-bar-hitbox-${i}`}
              />
              <rect x={x} y={y} width={barW} height={barH} rx="3"
                fill={active ? '#059669' : '#10B981'}
                opacity={active ? 1 : 0.85} />
              {showLabel && (
                <text x={x + barW / 2} y={h - 8} textAnchor="middle" fill="#64748B" fontSize="9" fontFamily="DM Sans">
                  {fmtDate(d.date)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hover !== null && data[hover] && (
        <div
          data-testid="signup-tooltip"
          className="absolute top-2 right-3 bg-slate-800 text-white rounded-xl px-3 py-2 shadow-xl text-xs font-body pointer-events-none z-10"
        >
          <p className="font-heading text-[11px] text-emerald-300 mb-0.5">{data[hover].date}</p>
          <p><span className="text-slate-400">New signups:</span> <span className="font-heading">{data[hover].count}</span></p>
          <p><span className="text-slate-400">Total users till date:</span> <span className="font-heading">{data[hover].cumulative_total ?? '—'}</span></p>
        </div>
      )}
    </div>
  );
}
